import React from 'react';
import { Shirt, BookOpen, ArrowUp, ArrowDown } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';

/**
 * Matriz de Kardex: prendas (filas) × movimientos agrupados por
 * consecutivo/factura (columnas). Cada celda muestra el impacto (+/-) y el
 * saldo resultante acumulado por prenda. Recibe la lista de movimientos.
 */
export const buildKardexMatrix = (movimientos = []) => {
  if (!movimientos.length) return { garments: [], events: [] };

  // Prendas únicas
  const garmentMap = new Map();
  movimientos.forEach((mov) => {
    const id = mov.tipo_prenda?.id;
    if (id && !garmentMap.has(id)) {
      garmentMap.set(id, {
        id,
        nombre: mov.tipo_prenda?.nombre || 'N/A',
        peso: mov.tipo_prenda?.peso
      });
    }
  });
  const garments = Array.from(garmentMap.values()).sort((a, b) =>
    (a.nombre || '').localeCompare(b.nombre || '')
  );

  // Agrupar movimientos en eventos (columnas) por consecutivo o factura;
  // si no tiene, cada movimiento es su propio evento.
  const eventMap = new Map();
  movimientos.forEach((mov) => {
    const key = mov.consecutivo || mov.numero_factura || mov._id;
    if (!eventMap.has(key)) {
      eventMap.set(key, {
        id: key,
        etiqueta: mov.consecutivo || mov.numero_factura || 'Movimiento',
        fecha: new Date(mov.fecha_movimiento),
        tipo: mov.tipo_movimiento,
        itemsMap: new Map()
      });
    }
    const event = eventMap.get(key);
    const fechaMov = new Date(mov.fecha_movimiento);
    if (fechaMov < event.fecha) event.fecha = fechaMov;

    const prendaId = mov.tipo_prenda?.id;
    const cantidad = mov.cantidad || 0; // ya viene con signo
    const prev = event.itemsMap.get(prendaId) || { impacto: 0 };
    event.itemsMap.set(prendaId, { impacto: prev.impacto + cantidad });
  });

  // Ordenar eventos cronológicamente (más antiguo primero)
  const events = Array.from(eventMap.values()).sort((a, b) => a.fecha - b.fecha);

  // Saldo resultante acumulado por prenda en cada movimiento
  garments.forEach((garment) => {
    let saldo = 0;
    events.forEach((event) => {
      const item = event.itemsMap.get(garment.id);
      if (item) {
        saldo += item.impacto;
        item.saldo = saldo;
      }
    });
  });

  return { garments, events };
};

const KardexMatrix = ({ movimientos = [], isLoading = false }) => {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-500">Cargando kardex...</p>
        </div>
      </div>
    );
  }

  if (!movimientos.length) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <BookOpen className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No hay movimientos para mostrar en el kardex</p>
        </CardContent>
      </Card>
    );
  }

  const { garments, events } = buildKardexMatrix(movimientos);

  return (
    <Card>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead className="sticky top-0 bg-gray-50 z-10">
              <tr>
                <th className="sticky left-0 bg-gray-100 border border-gray-300 p-3 text-left font-semibold text-sm min-w-[200px] z-20">
                  <div className="flex items-center gap-2">
                    <Shirt className="h-4 w-4 text-purple-600" />
                    Prenda
                  </div>
                </th>
                {events.map((event) => (
                  <th
                    key={event.id}
                    className={`border border-gray-300 p-2 text-center font-semibold text-xs min-w-[120px] ${
                      event.tipo === 'entrada' ? 'bg-green-50' : 'bg-orange-50'
                    }`}
                  >
                    <div className="flex flex-col items-center gap-1">
                      <div className="flex items-center gap-1">
                        {event.tipo === 'entrada' ? (
                          <ArrowUp className="h-3 w-3 text-green-600" />
                        ) : (
                          <ArrowDown className="h-3 w-3 text-orange-600" />
                        )}
                        <span className={event.tipo === 'entrada' ? 'text-green-700' : 'text-orange-700'}>
                          {event.tipo}
                        </span>
                      </div>
                      <div className="text-xs font-mono text-gray-600">
                        {String(event.etiqueta).substring(0, 15)}
                      </div>
                      <div className="text-xs text-gray-500">
                        {event.fecha.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: '2-digit' })}
                      </div>
                    </div>
                  </th>
                ))}
                <th className="bg-green-100 border border-gray-300 p-2 text-center font-semibold text-xs min-w-[100px]">
                  <div className="text-green-700">Total<br/>Entradas</div>
                </th>
                <th className="bg-orange-100 border border-gray-300 p-2 text-center font-semibold text-xs min-w-[100px]">
                  <div className="text-orange-700">Total<br/>Salidas</div>
                </th>
                <th className="sticky right-0 bg-blue-100 border border-gray-300 p-2 text-center font-semibold text-xs min-w-[100px] z-20">
                  <div className="text-blue-700">Saldo<br/>Actual</div>
                </th>
              </tr>
            </thead>
            <tbody>
              {garments.map((garment, gIdx) => {
                let totalEntradas = 0;
                let totalSalidas = 0;

                return (
                  <tr key={garment.id} className={gIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="sticky left-0 border border-gray-300 p-3 font-medium text-sm z-10 bg-inherit">
                      <div className="flex items-center gap-2">
                        <Shirt className="h-3 w-3 text-purple-600 flex-shrink-0" />
                        <span className="truncate">{garment.nombre}</span>
                      </div>
                      {garment.peso != null && (
                        <div className="text-xs text-gray-500 mt-1">
                          {garment.peso}g
                        </div>
                      )}
                    </td>
                    {events.map((event) => {
                      const itemData = event.itemsMap.get(garment.id);
                      const impacto = itemData?.impacto || 0;

                      if (impacto > 0) totalEntradas += impacto;
                      else if (impacto < 0) totalSalidas += Math.abs(impacto);

                      return (
                        <td
                          key={event.id}
                          className="border border-gray-300 p-2 text-center"
                        >
                          {impacto !== 0 ? (
                            <div className="flex flex-col items-center gap-0.5">
                              <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${
                                impacto > 0
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-orange-100 text-orange-700'
                              }`}>
                                {impacto > 0 ? '+' : ''}{impacto}
                              </div>
                              <span className="text-[10px] text-gray-500 font-mono">
                                ({itemData?.saldo})
                              </span>
                            </div>
                          ) : (
                            <span className="text-gray-300 text-xs">—</span>
                          )}
                        </td>
                      );
                    })}
                    <td className="bg-green-50 border border-gray-300 p-2 text-center font-bold text-sm">
                      <div className="text-green-700">+{totalEntradas}</div>
                    </td>
                    <td className="bg-orange-50 border border-gray-300 p-2 text-center font-bold text-sm">
                      <div className="text-orange-700">-{totalSalidas}</div>
                    </td>
                    <td className="sticky right-0 bg-blue-50 border border-gray-300 p-2 text-center font-bold text-sm z-10">
                      <div className={`inline-flex items-center gap-1 px-3 py-1 rounded-full ${
                        (totalEntradas - totalSalidas) < 0
                          ? 'bg-red-100 text-red-700'
                          : (totalEntradas - totalSalidas) > 0
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                        {(totalEntradas - totalSalidas) > 0 ? '+' : ''}{totalEntradas - totalSalidas}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="sticky bottom-0 bg-gray-100">
              <tr>
                <td className="sticky left-0 border border-gray-300 p-3 font-bold text-sm bg-gray-100 z-20">
                  Total por Movimiento
                </td>
                {events.map((event) => {
                  const total = Array.from(event.itemsMap.values()).reduce(
                    (sum, item) => sum + item.impacto,
                    0
                  );

                  return (
                    <td
                      key={event.id}
                      className={`border border-gray-300 p-2 text-center font-bold text-sm ${
                        event.tipo === 'entrada' ? 'bg-green-50' : 'bg-orange-50'
                      }`}
                    >
                      <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold ${
                        total < 0
                          ? 'bg-orange-200 text-orange-800'
                          : 'bg-green-200 text-green-800'
                      }`}>
                        {total > 0 ? '+' : ''}{total}
                      </div>
                    </td>
                  );
                })}
                <td className="bg-green-100 border border-gray-300 p-2 text-center font-bold text-sm">
                  <div className="text-green-800">
                    {garments.reduce((sum, g) => {
                      let total = 0;
                      events.forEach((e) => {
                        const imp = e.itemsMap.get(g.id)?.impacto || 0;
                        if (imp > 0) total += imp;
                      });
                      return sum + total;
                    }, 0)}
                  </div>
                </td>
                <td className="bg-orange-100 border border-gray-300 p-2 text-center font-bold text-sm">
                  <div className="text-orange-800">
                    {garments.reduce((sum, g) => {
                      let total = 0;
                      events.forEach((e) => {
                        const imp = e.itemsMap.get(g.id)?.impacto || 0;
                        if (imp < 0) total += Math.abs(imp);
                      });
                      return sum + total;
                    }, 0)}
                  </div>
                </td>
                <td className="sticky right-0 bg-blue-100 border border-gray-300 p-2 text-center font-bold text-sm z-20">
                  <div className="text-blue-800">
                    {garments.reduce((sum, g) => {
                      let ent = 0, sal = 0;
                      events.forEach((e) => {
                        const imp = e.itemsMap.get(g.id)?.impacto || 0;
                        if (imp > 0) ent += imp;
                        else if (imp < 0) sal += Math.abs(imp);
                      });
                      return sum + (ent - sal);
                    }, 0)}
                  </div>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Leyenda */}
        <div className="p-4 bg-gray-50 border-t border-gray-300">
          <div className="flex flex-wrap items-center justify-center gap-6 text-xs">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-100 border border-green-300"></div>
              <span className="text-gray-600">Entrada (+) suma al inventario</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-orange-100 border border-orange-300"></div>
              <span className="text-gray-600">Salida/Ajuste/Baja (-) resta del inventario</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-semibold text-gray-700">Saldo Actual:</span>
              <span className="text-gray-600">Impacto acumulado por prenda</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default KardexMatrix;
